import React, { useEffect, useMemo, useRef, useState } from 'react';

function cellsOnLine(a, b) {
  if (!a || !b) return [];
  const dr = Math.sign(b.r - a.r);
  const dc = Math.sign(b.c - a.c);
  const steps = Math.max(Math.abs(b.r - a.r), Math.abs(b.c - a.c));
  if (steps === 0) return [a];
  if (Math.abs(b.r - a.r) !== 0 && Math.abs(b.c - a.c) !== 0 && Math.abs(b.r - a.r) !== Math.abs(b.c - a.c)) {
    return [];
  }
  const out = [];
  for (let i = 0; i <= steps; i += 1) {
    out.push({ r: a.r + dr * i, c: a.c + dc * i });
  }
  return out;
}

function wordFromCells(grid, cells) {
  return cells.map(({ r, c }) => grid[r]?.[c] || '').join('');
}

function cellKey(cell) {
  return `${cell.r}-${cell.c}`;
}

function leftoverKey(cell) {
  return Array.isArray(cell) ? `${cell[0]}-${cell[1]}` : `${cell.r}-${cell.c}`;
}

function usedLetterFlags(letters, guess) {
  const flags = letters.map(() => false);
  String(guess || '').split('').forEach((ch) => {
    const index = letters.findIndex((letter, i) => letter === ch && !flags[i]);
    if (index >= 0) flags[index] = true;
  });
  return flags;
}

export default function WordSearchBoard({
  payload,
  savedFound,
  savedFoundCells,
  savedBonusGuess,
  savedBonusSolved,
  onSolved,
  onProgress,
}) {
  const grid = payload?.grid || [];
  const words = payload?.words || [];
  const bonusWord = String(payload?.bonus_word || '').toUpperCase();
  const themeLabel = payload?.theme_label || '';
  const leftoverLetters = payload?.leftover_letters || [];
  const leftoverKeys = useMemo(
    () => new Set((payload?.leftover_cells || []).map(leftoverKey)),
    [payload]
  );
  const [found, setFound] = useState(() => [...(savedFound || [])]);
  const [foundKeys, setFoundKeys] = useState(() => new Set(savedFoundCells || []));
  const [hover, setHover] = useState([]);
  const [bonusGuess, setBonusGuess] = useState(() => String(savedBonusGuess || '').toUpperCase());
  const [bonusError, setBonusError] = useState('');
  const startRef = useRef(null);
  const foundRef = useRef([...(savedFound || [])]);
  const foundKeysRef = useRef(new Set(savedFoundCells || []));
  const solvedRef = useRef(false);
  const wrapRef = useRef(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  const cols = grid[0]?.length || 1;
  const rows = grid.length || 1;

  const foundSet = useMemo(() => new Set(found), [found]);
  const wordsDone = words.length > 0 && words.every((w) => foundSet.has(w));
  const showBonus = wordsDone && !!bonusWord && leftoverLetters.length > 0;
  const usedFlags = useMemo(() => usedLetterFlags(leftoverLetters, bonusGuess), [leftoverLetters, bonusGuess]);

  const persist = (nextWords, nextKeys, nextGuess = bonusGuess, bonusSolved = solvedRef.current) => {
    onProgress?.({
      found: nextWords,
      foundCells: [...nextKeys],
      bonusGuess: nextGuess,
      bonusSolved,
    });
  };

  const completeBoard = (nextWords, nextKeys, nextGuess) => {
    if (solvedRef.current) return;
    solvedRef.current = true;
    persist(nextWords, nextKeys, nextGuess, true);
    onSolved?.();
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom
        : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      setPane({
        w: Math.max(160, el.clientWidth),
        h: Math.max(160, bottom - top - 16),
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const dialog = el.closest('[data-game-season-dialog]');
    const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
    if (scroller) ro.observe(scroller);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [payload, wordsDone, showBonus]);

  useEffect(() => {
    const allFound = words.length > 0 && words.every((w) => foundRef.current.includes(w));
    if (!allFound || solvedRef.current) return;
    if (!bonusWord) {
      completeBoard(foundRef.current, foundKeysRef.current, bonusGuess);
      return;
    }
    if (savedBonusSolved && String(savedBonusGuess || '').toUpperCase() === bonusWord) {
      completeBoard(foundRef.current, foundKeysRef.current, String(savedBonusGuess || '').toUpperCase());
    }
  }, [bonusWord, bonusGuess, savedBonusGuess, savedBonusSolved, words]);

  const markFound = (word, cells) => {
    if (foundRef.current.includes(word)) return;
    const next = [...foundRef.current, word];
    foundRef.current = next;
    setFound(next);
    const nextKeys = new Set(foundKeysRef.current);
    cells.forEach((cell) => nextKeys.add(cellKey(cell)));
    foundKeysRef.current = nextKeys;
    setFoundKeys(nextKeys);
    const allFound = words.every((w) => next.includes(w));
    if (allFound && !bonusWord) {
      completeBoard(next, nextKeys, bonusGuess);
      return;
    }
    persist(next, nextKeys);
  };

  const finish = (end) => {
    const cells = cellsOnLine(startRef.current, end);
    startRef.current = null;
    setHover([]);
    if (!cells.length) return;
    if (cells.some((cell) => leftoverKeys.has(cellKey(cell)))) return;
    const forward = wordFromCells(grid, cells);
    const backward = wordFromCells(grid, [...cells].reverse());
    const match = words.find((w) => w === forward || w === backward);
    if (match) markFound(match, cells);
  };

  const submitBonus = (raw) => {
    const guess = String(raw || '').toUpperCase().replace(/[^A-Z]/g, '');
    setBonusGuess(guess);
    setBonusError('');
    persist(foundRef.current, foundKeysRef.current, guess, false);
    if (!bonusWord || guess.length !== bonusWord.length) return;
    if (guess === bonusWord) {
      completeBoard(foundRef.current, foundKeysRef.current, guess);
      return;
    }
    setBonusError('Ce n’est pas le mot du thème. Réessayez.');
  };

  const gap = 2;
  const bonusReserve = showBonus ? 132 : 8;
  const cellFromW = pane.w ? (pane.w - gap * (cols - 1)) / cols : 34;
  const cellFromH = pane.h ? (pane.h - bonusReserve - gap * (rows - 1)) / rows : 34;
  const cellPx = Math.max(28, Math.min(80, Math.floor(Math.min(cellFromW, cellFromH) || 34)));
  const fontPx = Math.max(12, Math.round(cellPx * 0.42));
  const gridW = cellPx * cols + gap * (cols - 1);
  const gridH = cellPx * rows + gap * (rows - 1);

  return (
    <div className="flex flex-col sm:flex-row gap-4 w-full min-w-0 min-h-0">
      <div ref={wrapRef} className="w-full sm:w-2/3 min-w-0 flex flex-col items-center gap-3">
        <div
          className="grid select-none mx-auto"
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellPx}px)`,
            width: gridW,
            gap,
          }}
        >
          {grid.map((row, r) =>
            row.map((letter, c) => {
              const hovered = hover.some((cell) => cell.r === r && cell.c === c);
              const isFound = foundKeys.has(`${r}-${c}`);
              const isLeftover = showBonus && leftoverKeys.has(`${r}-${c}`);
              return (
                <button
                  key={`${r}-${c}`}
                  type="button"
                  className={`font-bold rounded ${
                    hovered
                      ? 'bg-[#f76f4b] text-white'
                      : isLeftover
                        ? 'bg-amber-200 text-amber-950 dark:bg-amber-600 dark:text-amber-50'
                        : isFound
                          ? 'bg-emerald-200 text-emerald-950 dark:bg-emerald-700 dark:text-emerald-50'
                          : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100'
                  }`}
                  style={{ width: cellPx, height: cellPx, fontSize: fontPx }}
                  onMouseDown={() => { startRef.current = { r, c }; setHover([{ r, c }]); }}
                  onMouseEnter={() => {
                    if (startRef.current) setHover(cellsOnLine(startRef.current, { r, c }));
                  }}
                  onMouseUp={() => finish({ r, c })}
                  onTouchStart={() => { startRef.current = { r, c }; setHover([{ r, c }]); }}
                  onTouchEnd={() => finish({ r, c })}
                >
                  {letter}
                </button>
              );
            })
          )}
        </div>
        {showBonus && (
          <div className="space-y-2 w-full max-w-full" style={{ maxWidth: gridW }}>
            <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">Lettres restantes</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Reformez un mot du thème {themeLabel || 'choisi'}.
            </p>
            <div className="flex flex-wrap gap-1">
              {leftoverLetters.map((letter, index) => (
                <button
                  key={`${letter}-${index}`}
                  type="button"
                  disabled={solvedRef.current || usedFlags[index]}
                  onClick={() => submitBonus(bonusGuess + letter)}
                  className={`w-9 h-9 rounded text-base font-bold ${
                    usedFlags[index]
                      ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                      : 'bg-amber-200 text-amber-950 dark:bg-amber-600 dark:text-amber-50'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={bonusGuess}
                maxLength={bonusWord.length}
                autoCapitalize="characters"
                onChange={(e) => submitBonus(e.target.value)}
                disabled={solvedRef.current}
                className="h-8 w-28 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 text-xs font-bold uppercase tracking-widest"
                placeholder={'·'.repeat(bonusWord.length)}
                aria-label="Mot formé avec les lettres restantes"
              />
              <button
                type="button"
                className="text-[11px] text-gray-500 underline"
                onClick={() => submitBonus('')}
                disabled={solvedRef.current || !bonusGuess}
              >
                Effacer
              </button>
            </div>
            {bonusError && <p className="text-[11px] text-red-500">{bonusError}</p>}
            {solvedRef.current && <p className="text-[11px] text-green-600">Mot du thème trouvé !</p>}
          </div>
        )}
      </div>
      <div className="w-full sm:w-1/3 min-w-0 text-sm">
        {themeLabel && (
          <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2">
            Thème : <span className="font-semibold text-gray-700 dark:text-gray-200">{themeLabel}</span>
          </p>
        )}
        <ul
          className="min-w-0 columns-2 gap-x-5 text-xs sm:text-sm [column-fill:auto]"
          style={{ height: gridH }}
        >
          {words.map((w) => (
            <li
              key={w}
              className={`mb-1 break-inside-avoid whitespace-nowrap ${foundSet.has(w) ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}
            >
              {w}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
